import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { AlertService } from '../../shared/alert/alert.service';
import { MultiTraitQueryService } from '../multi-trait-query.service';
import { Router } from '@angular/router';
import { TraitObservationResult, TraitResultRequest, TraitResultResponse } from '../trait.reference.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { MultiTraitQueryContext } from '../multi-trait-query-context';
import { SearchComposite } from '../../shared/model/search-composite';
import { GermplasmSearchRequest } from '../../entities/germplasm/germplasm-search-request.model';
import { GermplasmManagerContext } from '../../germplasm-manager/germplasm-manager.context';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { TraitFilterContext } from '../setup-trait-filter-step-panel/trait-filter-context';
import { TranslateService } from '@ngx-translate/core';
import { VIEW_NAMES_PERMISSIONS } from '../../shared/auth/permissions';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';

@Component({
    selector: 'jhi-step-display-results',
    styleUrl: './step-display-results.component.css',
    templateUrl: './step-display-results.component.html'
})
export class StepDisplayResultsComponent implements OnInit {
    protected readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;

    private subscriptionManager = new SubscriptionsManager();

    @Input() active = false;
    @Output() backToStepEnv = new EventEmitter<number>();
    @Output() backToBeginning = new EventEmitter<number>();

    public VIEW_NAMES_PERMISSIONS = [...VIEW_NAMES_PERMISSIONS];

    public traitResult: TraitResultResponse[];
    public isLoading: boolean;
    public totalItems: number;
    public itemsPerPage: number = 20;
    public page: number;
    public predicate: any;
    public isAscending: boolean;
    public isSelectAll: boolean;

    public selectedItems: Map<number, TraitResultResponse> = new Map<number, TraitResultResponse>();
    private allTraitResults: TraitResultResponse[] = [];
    private lastClickIndex: any;
    private noOfObs = '';
    private traitResultRequest: TraitResultRequest;

    constructor(private eventManager: JhiEventManager,
                private jhiLanguageService: JhiLanguageService,
                private alertService: AlertService,
                public traitFilterContext: TraitFilterContext,
                public queryContext: MultiTraitQueryContext,
                private germplasmManagerContext: GermplasmManagerContext,
                private multiTraitQueryService: MultiTraitQueryService,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private router: Router,
                private translateService: TranslateService) {

    }

    ngOnInit(): void {
        this.noOfObs = this.translateService.instant('multi-trait-query.panel.display.result.no.of.obs');
        this.clearSort();
        this.page = 1;
        this.totalItems = 0;
        this.traitResult = [];
        this.isSelectAll = false;
        this.eventManager.subscribe('on-germplasm-list-created', (event) => {
            this.traitResult = [];
            this.selectedItems.clear();
            this.backToBeginning.emit(0);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['active'] && this.active) {
            this.clearSort();
            this.page = 1;
            this.totalItems = 0;
            this.traitResult = [];
            this.isSelectAll = false;
            this.selectedItems.clear();
            this.buildTraitResultRequest();
            this.loadAll();
        }
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    private buildTraitResultRequest() {
        const environments = Array.from(this.queryContext.getEnvironmentSelectedItems().values()).map(env => ({
            environmentId: env.id,
            weight: env.weight
        }));
        this.traitResultRequest = new TraitResultRequest(environments, this.traitFilterContext.getNumericTraitFilters(), this.traitFilterContext.getCharacterTraitFilters(), this.traitFilterContext.getCategoricalTraitFilters());
    }

    isPageSelected() {
        return this.size() && this.traitResult.every((entry: TraitResultResponse) => Boolean(this.selectedItems.get(entry.germplasmId)));
    }

    private size() {
        return this.isSelectAll ? this.totalItems : this.selectedItems.size;
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.traitResult.forEach((entry) => this.selectedItems.delete(entry.germplasmId));
        } else {
            // check remaining items
            this.traitResult.forEach((entry) => this.selectedItems.set(entry.germplasmId, entry));
        }
    }

    onSelectAll(isSelectAll) {
        this.isSelectAll = !isSelectAll;
        this.clearSelectedItems();
        if (this.isSelectAll) {
            this.allTraitResults.forEach((item: TraitResultResponse) => {
                this.selectedItems.set(item.germplasmId, item);
            });
        }
    }

    private clearSelectedItems() {
        this.selectedItems.clear();
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.applySortAndPagination();
    }

    toggleSelect($event, index, entry: TraitResultResponse, checkbox = false) {
        if (this.isSelectAll) {
            return;
        }
        if (!$event.ctrlKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.traitResult.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(entry.germplasmId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.germplasmId);
            } else {
                this.selectedItems.set(item.germplasmId, item);
            }
        }
    }

    isSelected(entry: TraitResultResponse) {
        return this.selectedItems.get(entry.germplasmId);
    }

    private loadAll() {
        this.isLoading = true;
        this.subscriptionManager.add = this.multiTraitQueryService.getTraitResults(this.traitResultRequest,
            null, true).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<TraitResultResponse[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        this.page = page;
        this.applySortAndPagination();
    }

    transition() {
        this.applySortAndPagination();
    }

    resetTable() {
        this.page = 1;
        this.isSelectAll = false;
        this.clearSelectedItems();
        this.applySortAndPagination();
    }

    trackId(index: number, item: any) {
        return item.id;
    }

    isSaveEnabled(): boolean {
        return this.selectedItems.size > 0;
    }

    onSave(): void {
        const searchComposite = new SearchComposite<GermplasmSearchRequest, number>();
        searchComposite.itemIds = this.getSelectedItemIds();
        this.germplasmManagerContext.searchComposite = searchComposite;

        this.router.navigate(['/', { outlets: { popup: 'germplasm-list-creation-dialog' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge'
        });
    }

    onBack(): void {
        this.backToStepEnv.emit(2);
    }

    showTraitHeader(obs: TraitObservationResult) {
        return obs.name + this.noOfObs;
    }

    showRoundOff(value: number) {
        return value.toFixed(1);
    }

    private clearSort() {
        this.predicate = 'combinedScore';
        this.isAscending = false;
    }

    private onSuccess(data: TraitResultResponse[], headers) {
        this.totalItems = parseInt(headers.get('X-Total-Count'), 10);
        this.allTraitResults  = data || [];
        this.applySortAndPagination();
    }

    private getSelectedItemIds() {
        return Array.from(this.selectedItems.values()).map((g) => Number(g.germplasmId));
    }

    private applySortAndPagination() {
        let data = [...this.allTraitResults];

        if (this.predicate) {
            data.sort((a: TraitResultResponse, b: TraitResultResponse) => {
                let valA = (a as any)[this.predicate];
                let valB = (b as any)[this.predicate];

                if (this.predicate.startsWith('obs-')) {
                    const [, field, indexStr] = this.predicate.split('-');
                    const index = parseInt(indexStr, 10);

                    const findObsValue = (res: TraitResultResponse) => {
                        const obs =
                            res.traitObservations.length > 0 &&
                            res.traitObservations.length > index ? res.traitObservations[index] : null;
                        if (!obs) {
                            return null;
                        }
                        if (field === 'noOfObs') {
                            return Number(obs.noOfObs);
                        }
                        if (field === 'successRatio') {
                            return Number(obs.successRatio);
                        }
                        return null;
                    };

                    valA = findObsValue(a);
                    valB = findObsValue(b);
                }

                if (valA == null && valB == null) {
                    return 0;
                }
                if (valA == null) {
                    return 1;
                }
                if (valB == null) {
                    return -1;
                }

                if (valA < valB) {
                    return this.isAscending ? -1 : 1;
                }
                if (valA > valB) {
                    return this.isAscending ? 1 : -1;
                }
                return 0;
            });
        }

        const start = (this.page - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        this.traitResult = data.slice(start, end);
    }

}
